import Debug from 'debug';
import { PubHubError } from './errors.mjs';
import { PageAction } from './page-actions.mjs';

const debug = Debug("pubhub:metrics");
/**
 * Metrics manager singleton.
 *
 * Used to track errors and other metrics via New Relic.
 * @alpha
 */
const metrics = Object.freeze({
    setCustomAttribute(name, value) {
        debug("setCustomAttribute", name, value, { newrelic: !!window?.newrelic });
        window?.newrelic?.setCustomAttribute(name, value);
    },
    noticeError(e, customAttributes) {
        // Some browsers need us to throw and catch the error
        // @see https://docs.newrelic.com/docs/browser/new-relic-browser/browser-agent-spa-api/noticeerror-browser-agent-api/#browser-limits
        try {
            throw e;
        }
        catch (e) {
            const err = e instanceof Error ? e : String(e);
            const cause = (e instanceof PubHubError ? e.cause : undefined)?.message ?? "";
            const combinedCustomAttributes = {
                // Potentially add any custom attributes in the error object.
                ...(e instanceof PubHubError ? e.customAttributes : {}),
                // Override with parameter custom attributes.
                ...customAttributes,
                cause,
            };
            console.error(e, { combinedCustomAttributes });
            window?.newrelic?.noticeError(err, combinedCustomAttributes);
        }
        window?.newrelic?.interaction().save();
    },
    finished(timestamp) {
        debug("page rendering finished", timestamp || Date.now(), { newrelic: !!window?.newrelic });
        window?.newrelic?.finished(timestamp);
        // make sure the interaction is sent to newrelic!
        window?.newrelic?.interaction().save();
        // and at this point, the interaction should end.
        window?.newrelic?.interaction().end();
    },
    addPubHubPageAction(action, customAttributes) {
        metrics.addPageAction(action, customAttributes);
    },
    addPageAction(name, customAttributes) {
        debug("addPageAction", name, customAttributes, { newrelic: !!window?.newrelic });
        window?.newrelic?.addPageAction(name, customAttributes);
        window?.newrelic?.interaction().save();
    },
    registerSdkReady({ appContext, edge }) {
        metrics.setCustomAttribute("pubhub:appId", String(appContext?.appId));
        metrics.setCustomAttribute("pubhub:appLocale", String(appContext?.appLocale));
        metrics.setCustomAttribute("pubhub:deviceLocale", String(appContext?.deviceLocale));
        metrics.setCustomAttribute("pubhub:publishingLocale", String(appContext?.publishingLocale));
        metrics.setCustomAttribute("pubhub:clientId", String(edge.clientId));
        metrics.setCustomAttribute("pubhub:clientRegion", String(edge.clientRegion));
        metrics.addPubHubPageAction(PageAction.SdkReady);
    },
    registerFrameworkReady() {
        metrics.addPubHubPageAction(PageAction.FrameworkReady);
    },
    registerChannelDataReady() {
        metrics.addPubHubPageAction(PageAction.ChannelDataReady);
    },
    registerChannelReady() {
        metrics.addPubHubPageAction(PageAction.ChannelReady);
        metrics.finished();
    },
});

export { metrics };
//# sourceMappingURL=index.mjs.map
